
import { useState } from 'react';

interface Product {
  id: string;
  name: string;
  price: number;
  collection: string;
  color: string;
  material: string;
  category: string;
  images: string[];
  description: string;
  care: string;
}

interface OrderFormProps {
  product: Product;
  onClose: () => void;
}

export default function OrderForm({ product, onClose }: OrderFormProps) {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    direccion: '',
    localidad: '',
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const submitData = new URLSearchParams();
      submitData.append('nombre', formData.nombre);
      submitData.append('email', formData.email);
      submitData.append('direccion', formData.direccion);
      submitData.append('localidad', formData.localidad);
      submitData.append('producto', product.name);
      submitData.append('precio', product.price.toString());
      submitData.append('mensaje', formData.mensaje);

      const response = await fetch('https://readdy.ai/api/form/d473r8d34biqimnfocd0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData.toString()
      });

      if (response.ok) {
        setShowSuccess(true);
        setTimeout(() => {
          onClose();
        }, 3000);
      }
    } catch (error) {
      console.error('Error al enviar el pedido:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-check-line text-3xl text-green-600"></i>
          </div>
          <h3 className="text-xl font-bold text-black mb-2" style={{ fontFamily: '"Playfair Display", serif' }}>
            ¡Pedido enviado!
          </h3>
          <p className="text-gray-600 mb-4" style={{ fontFamily: '"Lato", sans-serif' }}>
            Tu pedido fue enviado. Macarena te contactará por WhatsApp para coordinar pago y envío.
          </p>
          <button 
            onClick={onClose}
            className="bg-[#C6A45C] text-white px-6 py-2 rounded hover:bg-[#B8954F] transition-colors duration-300 whitespace-nowrap"
          >
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
          <h2 
            className="text-2xl font-bold text-black"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            Realizar Pedido
          </h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-300"
          >
            <i className="ri-close-line text-2xl text-black"></i>
          </button>
        </div>

        <div className="p-6">
          {/* Product Summary */}
          <div className="bg-[#FAFAFA] p-4 rounded-lg mb-6">
            <h3 className="font-medium text-black mb-2">Producto seleccionado:</h3>
            <p className="text-lg font-bold text-[#C6A45C]">{product.name}</p>
            <p className="text-gray-600">
              {new Intl.NumberFormat('es-AR', {
                style: 'currency',
                currency: 'ARS',
                minimumFractionDigits: 0
              }).format(product.price)}
            </p>
          </div>

          <form id="pedido-form" onSubmit={handleSubmit} data-readdy-form>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="nombre" className="block text-sm font-medium text-black mb-2">
                  Nombre completo *
                </label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C6A45C] focus:border-transparent text-sm"
                  placeholder="Tu nombre completo"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-black mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C6A45C] focus:border-transparent text-sm"
                  placeholder="tu@email.com"
                />
              </div>

              <div>
                <label htmlFor="direccion" className="block text-sm font-medium text-black mb-2">
                  Dirección de envío *
                </label>
                <input
                  type="text"
                  id="direccion"
                  name="direccion"
                  value={formData.direccion}
                  onChange={handleChange}
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C6A45C] focus:border-transparent text-sm"
                  placeholder="Calle y número"
                />
              </div>

              <div>
                <label htmlFor="localidad" className="block text-sm font-medium text-black mb-2">
                  Localidad *
                </label>
                <input
                  type="text"
                  id="localidad"
                  name="localidad"
                  value={formData.localidad}
                  onChange={handleChange}
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C6A45C] focus:border-transparent text-sm"
                  placeholder="Ciudad, Provincia"
                />
              </div>
            </div>

            <div className="mt-6">
              <label htmlFor="mensaje" className="block text-sm font-medium text-black mb-2">
                Mensaje adicional
              </label>
              <textarea
                id="mensaje"
                name="mensaje"
                value={formData.mensaje}
                onChange={handleChange}
                maxLength={500}
                rows={4}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C6A45C] focus:border-transparent text-sm resize-none"
                placeholder="Comentarios adicionales sobre tu pedido (máximo 500 caracteres)"
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.mensaje.length}/500 caracteres
              </p>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-black text-white py-3 px-6 text-lg font-medium hover:bg-gray-900 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Enviando...' : 'Enviar pedido a Macarena'}
              </button>
              
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-200 text-black py-3 px-6 text-lg font-medium hover:bg-gray-300 transition-colors duration-300 whitespace-nowrap"
              >
                Cancelar
              </button>
            </div>
          </form>

          <div className="mt-6 p-4 bg-[#FAFAFA] rounded-lg">
            <div className="flex items-start space-x-3">
              <i className="ri-information-line text-[#C6A45C] text-xl mt-0.5"></i>
              <div>
                <h4 className="font-medium text-black mb-1">Información de envío</h4>
                <p className="text-sm text-gray-600" style={{ fontFamily: '"Lato", sans-serif' }}>
                  Los envíos se realizan por Uber Express gestionados por Macarena. El costo del Uber es a cargo del comprador. 
                  Recibirás el link de seguimiento para monitorear tu pedido en tiempo real.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
